import { Box, Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import BgImg from "./assets/imgs/bg.svg";
import ComingSoon from "./components/ComingSoon";
import Header from "./components/Header";
import SwapCard from "./components/SwapCard";
import { apiFetch } from "./utils/apiClient";
import { clearSwapSession, readSwapSession } from "./utils/swapSessionStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const XRPL_RPC_URL = process.env.REACT_APP_XRPL_RPC_URL || "https://s1.ripple.com:51234/";
const TRACKED_TOKENS = ["USDT", "XRPH", "XRPHAI", "RLUSD"] as const;

type XrplJsonRpcResponse<T> = {
  result?: T;
};

type AccountInfoResult = {
  account_data?: { Balance?: string };
};

type AccountLinesResult = {
  lines?: Array<{ currency?: string; balance?: string }>;
};

type GatewayBalancesResult = {
  obligations?: Record<string, string>;
  assets?: Record<string, Array<{ currency?: string; value?: string }>>;
};

type PriceResponse = {
  usd?: Record<string, number>;
};

type BackendBalancesResponse = {
  balances?: Array<{
    coin?: {
      symbol?: string;
    };
    balance?: number;
  }>;
  totalBalanceInUSD?: number;
};

const decodeCurrency = (currency: string) => {
  if (!currency) return "";
  if (currency.length === 40) {
    try {
      const decoded = currency
        .match(/.{1,2}/g)
        ?.map((hex) => String.fromCharCode(parseInt(hex, 16)))
        .join("")
        .replace(/\0/g, "")
        .trim();
      return (decoded || currency).toUpperCase();
    } catch {
      return currency.toUpperCase();
    }
  }
  return currency.toUpperCase();
};

const roundTo2 = (value: number) => Number(value.toFixed(2));

function SwapApp() {
  const [bootstrap] = useState(() => {
    const s = readSwapSession();
    if (s?.token && s.channel_data_hash && s.wallet_address) {
      return { connected: true as const, address: s.wallet_address };
    }
    return { connected: false as const, address: "" };
  });
  const [connectRequestNonce, setConnectRequestNonce] = useState(0);
  const [isWalletConnected, setIsWalletConnected] = useState(bootstrap.connected);
  const [walletAddress, setWalletAddress] = useState(bootstrap.address);
  const [totalBalanceInUSD, setTotalBalanceInUSD] = useState(0);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!isWalletConnected || !walletAddress || !API_BASE_URL) {
        return;
      }

      try {
        setIsBalanceLoading(true);
        const [accountInfoRes, accountLinesRes, gatewayBalancesRes, pricesRes] =
          await Promise.allSettled([
            fetch(XRPL_RPC_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                method: "account_info",
                params: [{ account: walletAddress, ledger_index: "validated" }],
              }),
            }),
            fetch(XRPL_RPC_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                method: "account_lines",
                params: [{ account: walletAddress, ledger_index: "validated", limit: 400 }],
              }),
            }),
            fetch(XRPL_RPC_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                method: "gateway_balances",
                params: [{ account: walletAddress, ledger_index: "validated" }],
              }),
            }),
            apiFetch(`${API_BASE_URL}/swap/getPrices`),
          ]);

        const isXrplFlowAvailable =
          accountInfoRes.status === "fulfilled" &&
          accountLinesRes.status === "fulfilled" &&
          pricesRes.status === "fulfilled" &&
          accountInfoRes.value.ok &&
          accountLinesRes.value.ok &&
          pricesRes.value.ok;

        // Browser XRPL RPC can fail due CORS/network policy. Fall back to backend balances endpoint.
        if (!isXrplFlowAvailable) {
          const fallbackResponse = await apiFetch(
            `${API_BASE_URL}/swap/getBalances/${encodeURIComponent(walletAddress)}`
          );
          if (!fallbackResponse.ok) {
            throw new Error("Failed to fetch wallet balance from fallback endpoint.");
          }
          const fallbackData = (await fallbackResponse.json()) as BackendBalancesResponse;
          setTotalBalanceInUSD(Number(fallbackData?.totalBalanceInUSD || 0));
          return;
        }

        const accountInfo =
          (await accountInfoRes.value.json()) as XrplJsonRpcResponse<AccountInfoResult>;
        const accountLines =
          (await accountLinesRes.value.json()) as XrplJsonRpcResponse<AccountLinesResult>;
        const gatewayBalances =
          gatewayBalancesRes.status === "fulfilled" && gatewayBalancesRes.value.ok
            ? ((await gatewayBalancesRes.value.json()) as XrplJsonRpcResponse<GatewayBalancesResult>)
          : undefined;
        const prices = (await pricesRes.value.json()) as PriceResponse;

        const tokenBalances: Record<string, number> = {
          XRP: 0,
          USDT: 0,
          XRPH: 0,
          XRPHAI: 0,
          RLUSD: 0,
        };

        const xrpDrops = Number(accountInfo?.result?.account_data?.Balance || 0);
        const xrpOnChain = xrpDrops / 1_000_000;
        tokenBalances.XRP = Math.max(0, xrpOnChain - 1.8);

        for (const line of accountLines?.result?.lines || []) {
          const symbol = decodeCurrency(line.currency || "");
          if (!TRACKED_TOKENS.includes(symbol as (typeof TRACKED_TOKENS)[number])) continue;
          const value = Number(line.balance || 0);
          if (value > 0) {
            tokenBalances[symbol] += value;
          }
        }

        const obligations = gatewayBalances?.result?.obligations || {};
        for (const [currency, balance] of Object.entries(obligations)) {
          const symbol = decodeCurrency(currency);
          if (!TRACKED_TOKENS.includes(symbol as (typeof TRACKED_TOKENS)[number])) continue;
          if (tokenBalances[symbol] > 0) continue;
          const value = Number(balance || 0);
          if (value > 0) tokenBalances[symbol] = value;
        }

        const assets = gatewayBalances?.result?.assets || {};
        for (const lines of Object.values(assets)) {
          for (const line of lines) {
            const symbol = decodeCurrency(line.currency || "");
            if (!TRACKED_TOKENS.includes(symbol as (typeof TRACKED_TOKENS)[number])) continue;
            if (tokenBalances[symbol] > 0) continue;
            const value = Number(line.value || 0);
            if (value > 0) tokenBalances[symbol] = value;
          }
        }

        const usd = prices?.usd || {};
        const totalUsdRaw =
          tokenBalances.XRP * Number(usd.XRP || 0) +
          tokenBalances.XRPH * Number(usd.XRPH || 0) +
          tokenBalances.XRPHAI * Number(usd.XRPHAI || 0) +
          tokenBalances.USDT * Number(usd.USDT || 0) +
          tokenBalances.RLUSD * Number(usd.RLUSD || 0);

        setTotalBalanceInUSD(roundTo2(totalUsdRaw));
      } catch {
        setTotalBalanceInUSD(0);
      } finally {
        setIsBalanceLoading(false);
      }
    };

    fetchBalance();
  }, [isWalletConnected, walletAddress]);

  return (
    <Box
      bgColor="#181818"
      minH="100vh"
      bgImage={BgImg}
      bgSize="contain"
      bgRepeat="no-repeat"
      bgPos="center"
    >
      <Header
        isConnected={isWalletConnected}
        walletAddress={walletAddress}
        totalBalanceInUSD={totalBalanceInUSD}
        isBalanceLoading={isBalanceLoading}
        onConnectWallet={() => setConnectRequestNonce((prev) => prev + 1)}
        onDisconnectWallet={() => {
          clearSwapSession();
          setIsWalletConnected(false);
          setWalletAddress("");
          setTotalBalanceInUSD(0);
        }}
      />
      <Flex
        justify="center"
        align="center"
        mt={{ base: "50px", md: "70px" }}
        px={{ base: "16px", md: "64px" }}
      >
        <SwapCard
          connectRequestNonce={connectRequestNonce}
          isConnected={isWalletConnected}
          onWalletConnected={(connectedWalletAddress) => {
            if (connectedWalletAddress) {
              setIsWalletConnected(true);
              setWalletAddress(connectedWalletAddress);
            } else {
              clearSwapSession();
              setIsWalletConnected(false);
              setWalletAddress("");
              setTotalBalanceInUSD(0);
            }
          }}
        />
      </Flex>
    </Box>
  );
}

// Swap is paused. Nothing below has been removed: SwapApp still holds the full
// swap UI and every handler it needs. Flip IS_SWAP_PAUSED back to false to bring
// the swap screen back exactly as it was.
// Typed as boolean (not the literal `true`) so the SwapApp branch stays
// reachable for TypeScript and lint.
const IS_SWAP_PAUSED: boolean = true;

function App() {
  if (IS_SWAP_PAUSED) {
    return <ComingSoon />;
  }

  return <SwapApp />;
}

export default App;
